import { Box, Button, CircularProgress, Divider, Grid, } from "@mui/material";
import { paramsBuyerListingValues, ROUTES } from "../../../utils/constants";
import React from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { BarChartPro } from '@mui/x-charts-pro';
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import { useExceptionsData, useExceptionsIncrementalData } from "../../../reactQuery/homeSites/exceptions/homeSiteExceptions";
import { useSelector } from "react-redux";
import { getAuth } from "../../../redux/auth/authSlice";

interface MetricsCardCountProps {
    count: number,
    homeSiteIds: string[]
}

interface ChartsComponentProps {
    isloading?: boolean;
    isIncrementalLoading?: boolean;
    metrics: {
        closingsIn14Days: MetricsCardCountProps;
        weeklyUpdate: MetricsCardCountProps;
        closingsThisMonth: MetricsCardCountProps;
        closingsIn60Days?: MetricsCardCountProps;
        closingsIn30Days?: MetricsCardCountProps;
        ecoeMismatch?: MetricsCardCountProps;
    };
    navigate: (path: string) => void;
}



const ChartComponent: React.FC<ChartsComponentProps> = ({ metrics, navigate ,isloading, isIncrementalLoading}) => {

    const { userInfo } = useSelector(getAuth);
    const { data: exceptions } = useExceptionsData(userInfo.userDivCode)


    const homeSitesWithExceptions = (homeSiteIds: string[] = []) => {
        if (!exceptions?.data || !homeSiteIds.length) return 0;
        return homeSiteIds.filter(homeSiteId =>
            exceptions.data.some((exception: any) => exception.homeSiteId === homeSiteId && exception.status === true)).length;
        };

    useExceptionsIncrementalData(userInfo.userDivCode, exceptions?.continuationToken ?? "")

    const maxValue = Math.max(
        metrics.closingsIn14Days.count,
        metrics.closingsThisMonth.count,
        metrics.closingsIn30Days?.count ?? 0,
        metrics.closingsIn60Days?.count ?? 0
    );
    const dynamicMax = maxValue > 5 ? maxValue + 10 : 10;
    const dynamicMin = 0;

    return (
        <Grid sx={{border: "1px solid #E7ECF0 ", borderRadius: "6px", background: "#FFFFFF", padding: "16px",}} flex={0.55}>
            <Box sx={{ padding: "10px", display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <span style={{ fontWeight: "bold", fontSize: "16px" }}>Homesite Closings Overview</span>
                <Button
                    sx={{ 
                        textTransform: "none",
                        fontWeight: "bold",
                            border: "2px solid #005DAA",
                            "&:hover": {
                                border: "2px solid #005DAA",
                            }, }}
                    variant="outlined"
                    endIcon={<ArrowForwardIcon />}
                    size="small"
                    onClick={() =>
                        navigate(
                          `${ROUTES.closings.buyerListing.route}?timePeriod=${paramsBuyerListingValues.allBuyers}`
                        )
                      }
                >
                    Homesite Closings Details
                </Button>
            </Box>
            <Divider />
            <Box>
                {exceptions ? (
                    <BarChartPro
                        dataset={[]}
                        height={600}
                        yAxis={[
                            {
                                min: dynamicMin,
                                max: dynamicMax,
                                tickNumber: 10,
                                label: "Homesites Count",
                                labelStyle: {
                                    fontWeight: "500",
                                    fontSize: "14px",
                                    color: "#5B7282",
                                },
                            },
                        ]}
                        xAxis={[
                            {
                                scaleType: "band",
                                data: ["In 14 Days", "This Month", "In 30 Days", "In 60 Days"],
                                label: "Homesite Closings",
                                labelStyle: {
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#5B7282 !important",
                                },

                            },

                        ]}
                        series={[
                            {
                                data: [
                                    metrics.closingsIn14Days.count ?? 0,
                                    metrics.closingsThisMonth.count ?? 0,
                                    metrics.closingsIn30Days?.count ?? 0,
                                    metrics.closingsIn60Days?.count ?? 0,
                                    metrics.ecoeMismatch?.count ?? 0,
                                ],
                                label: "Total Homesites",
                                color: "#97B5FF",
                            },
                            {
                                data: [
                                    homeSitesWithExceptions(metrics.closingsIn14Days.homeSiteIds),
                                    homeSitesWithExceptions(metrics.closingsThisMonth.homeSiteIds),
                                    homeSitesWithExceptions(metrics.closingsIn30Days?.homeSiteIds),
                                    homeSitesWithExceptions(metrics.closingsIn60Days?.homeSiteIds),
                                ],
                                label: "Homesites with Exceptions",
                                color: "#C93939",
                            },
                        ]}
                        slotProps={{
                            legend: {
                                position: { vertical: 'top', horizontal: 'left' },
                                itemGap: 40,
                                markGap: 16,
                                padding: 2,
                                labelStyle: {
                                    fontSize: 12,
                                    fontWeight: "700",
                                    fill: '#1C2B36',
                                },

                            },
                        }}

                        sx={{

                            [`.${axisClasses.left} .${axisClasses.label}`]: {
                                transform: "translate(-10px, 0)",
                            },
                        }}

                        grid={{ horizontal: true }}
                        loading={!exceptions}
                        margin={{
                            top: 90,
                            left: 54
                        }}
                    />
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        {(isloading || isIncrementalLoading) ? <CircularProgress /> : "No data available"}
                    </Box>
                )}
            </Box>
        </Grid>
    );
};

export default ChartComponent;